/**
 * Evaluate KB contextModifiers (and legacy edge demographicModifiers) against PatientContextProfile.
 */

/**
 * @param {{ factor?: string, values?: string[] }[]} conditions
 * @param {import('./ddxTypes.js').PatientContextProfile} context
 * @returns {boolean}
 */
export function allConditionsMet(conditions, context) {
  if (!Array.isArray(conditions) || conditions.length === 0) return true;
  const ctx = context || {};
  return conditions.every(({ factor, values }) => {
    if (factor == null || !Array.isArray(values) || values.length === 0) return false;
    const val = ctx[factor];
    if (Array.isArray(val)) return val.some((v) => values.includes(v));
    return values.includes(val);
  });
}

/**
 * Legacy edge demographicModifiers → synthetic context modifier blocks (ageBand only).
 * @param {object} edge
 * @returns {object[]}
 */
export function getEdgeContextModifierList(edge) {
  if (Array.isArray(edge?.contextModifiers) && edge.contextModifiers.length > 0) {
    return edge.contextModifiers;
  }
  return legacyDemographicModifiersAsContext(edge);
}

/**
 * @param {object} edge
 * @param {import('./ddxTypes.js').PatientContextProfile} context
 */
export function applyContextModifiersToEdge(edge, context) {
  return applyContextModifiers({ contextModifiers: getEdgeContextModifierList(edge) }, context);
}

export function legacyDemographicModifiersAsContext(edge) {
  const mods = Array.isArray(edge?.demographicModifiers) ? edge.demographicModifiers : [];
  /** @type {object[]} */
  const out = [];
  for (const m of mods) {
    const bands = Array.isArray(m.ageBands) ? m.ageBands : [];
    if (bands.length === 0) continue;
    out.push({
      conditions: [{ factor: 'ageBand', values: bands }],
      priorProbabilityMultiplier: m.priorProbabilityMultiplier,
      strengthAdjustment: typeof m.strengthAdjustment === 'number' ? m.strengthAdjustment : 0,
      absenceAdjustmentOverride: m.absenceAdjustmentOverride != null ? m.absenceAdjustmentOverride : null,
      note: m.note,
      surfaceNote: m.surfaceNote,
      affectsExpectedFeatures: m.affectsExpectedFeatures,
    });
  }
  return out;
}

/**
 * @param {object} kbNode - diagnosis or edge document (must have optional `contextModifiers` array)
 * @param {import('./ddxTypes.js').PatientContextProfile} context
 * @returns {{
 *   priorMultiplier: number,
 *   strengthAdjustment: number,
 *   absenceAdjustmentOverride: number | null,
 *   triggeredNotes: string[],
 * }}
 */
export function applyContextModifiers(kbNode, context) {
  let priorMultiplier = 1.0;
  let strengthAdjustment = 0;
  /** @type {number | null} */
  let absenceAdjustmentOverride = null;
  const triggeredNotes = [];

  const mods = Array.isArray(kbNode?.contextModifiers) ? kbNode.contextModifiers : [];

  for (const modifier of mods) {
    if (!modifier || typeof modifier !== 'object') continue;
    if (!allConditionsMet(modifier.conditions, context)) continue;
    const pm = modifier.priorProbabilityMultiplier;
    if (typeof pm === 'number' && Number.isFinite(pm) && pm > 0) priorMultiplier *= pm;
    const sa = modifier.strengthAdjustment;
    if (typeof sa === 'number' && Number.isFinite(sa)) strengthAdjustment += sa;
    if (modifier.absenceAdjustmentOverride != null) {
      const o = Number(modifier.absenceAdjustmentOverride);
      if (Number.isFinite(o)) {
        absenceAdjustmentOverride =
          absenceAdjustmentOverride == null ? o : Math.max(absenceAdjustmentOverride, o);
      }
    }
    if (modifier.surfaceNote && modifier.note) triggeredNotes.push(String(modifier.note));
  }

  return { priorMultiplier, strengthAdjustment, absenceAdjustmentOverride, triggeredNotes };
}
